import type { NotifyMessageOptions } from '@/shared/types/message';
import type {
  NotificationContentProps,
  NotificationEventsProps,
} from '@/modules/Notification/types/notification';

/**
 * Fusiona API agrupada (`events`, `cta`) y props planas deprecadas en un único
 * `NotifyMessageOptions` con campos planos de cola y bloques de tarjeta coherentes.
 * Prevalece el grupo sobre la raíz plana cuando ambos están presentes.
 *
 * @param options - Payload crudo de `notify.message`.
 * @returns Opciones normalizadas listas para merge de tarjeta y encolado.
 */
export function normalizeNotifyMessageOptions(options: NotifyMessageOptions): NotifyMessageOptions {
  const result: NotifyMessageOptions = { ...options };

  const rootEvents = options.events ?? {};
  const nestedEvents = options.notification?.events ?? {};

  const onClick = rootEvents.onClick ?? nestedEvents.onClick ?? options.onClick;
  const onCloseCard = rootEvents.onClose ?? nestedEvents.onClose;
  const onCloseFlat = options.onClose;

  if (onClick !== undefined) result.onClick = onClick;
  if (onCloseFlat !== undefined) result.onClose = onCloseFlat;

  const contentKind = options.content?.kind ?? options.notification?.content?.kind ?? 'default';
  const isCtaKind = contentKind === 'cta';

  const headerPrimary = rootEvents.primaryButton ?? nestedEvents.primaryButton;
  const headerSecondary = rootEvents.secondaryButton ?? nestedEvents.secondaryButton;

  const primaryButtonText =
    headerPrimary?.label ??
    rootEvents.primaryButtonText ??
    nestedEvents.primaryButtonText ??
    options.primaryButtonText;
  const secondaryButtonText =
    headerSecondary?.label ??
    rootEvents.secondaryButtonText ??
    nestedEvents.secondaryButtonText ??
    options.secondaryButtonText;

  const onPrimaryClick =
    headerPrimary?.onClick ??
    rootEvents.onPrimaryClick ??
    nestedEvents.onPrimaryClick ??
    options.onPrimaryClick;
  const onSecondaryClick =
    headerSecondary?.onClick ??
    rootEvents.onSecondaryClick ??
    nestedEvents.onSecondaryClick ??
    options.onSecondaryClick;

  if (!isCtaKind) {
    if (primaryButtonText !== undefined) result.primaryButtonText = primaryButtonText;
    if (secondaryButtonText !== undefined) result.secondaryButtonText = secondaryButtonText;
    if (onPrimaryClick !== undefined) result.onPrimaryClick = onPrimaryClick;
    if (onSecondaryClick !== undefined) result.onSecondaryClick = onSecondaryClick;
  }

  const ctaGroup = options.cta;
  const contentCta = options.content?.cta ?? options.notification?.content?.cta;
  const ctaPrimaryBtn = ctaGroup?.primaryButton ?? contentCta?.primaryButton;
  const ctaSecondaryBtn = ctaGroup?.secondaryButton ?? contentCta?.secondaryButton;

  const ctaPrimaryLabel =
    ctaPrimaryBtn?.label ?? contentCta?.primaryLabel ?? rootEvents.primaryButtonText;
  const ctaSecondaryLabel =
    ctaSecondaryBtn?.label ?? contentCta?.secondaryLabel ?? rootEvents.secondaryButtonText;

  const ctaOnPrimary =
    ctaPrimaryBtn?.onClick ??
    (isCtaKind ? onPrimaryClick : undefined) ??
    rootEvents.onPrimaryClick ??
    nestedEvents.onPrimaryClick;
  const ctaOnSecondary =
    ctaSecondaryBtn?.onClick ??
    (isCtaKind ? onSecondaryClick : undefined) ??
    rootEvents.onSecondaryClick ??
    nestedEvents.onSecondaryClick;

  if (isCtaKind || ctaGroup || contentCta) {
    const mergedContent = { ...options.content };
    const mergedCta = { ...contentCta };

    if (ctaPrimaryLabel !== undefined) mergedCta.primaryLabel = ctaPrimaryLabel;
    if (ctaSecondaryLabel !== undefined) mergedCta.secondaryLabel = ctaSecondaryLabel;
    if (ctaPrimaryBtn) mergedCta.primaryButton = ctaPrimaryBtn;
    if (ctaSecondaryBtn) mergedCta.secondaryButton = ctaSecondaryBtn;

    if (Object.keys(mergedCta).length > 0) {
      mergedContent.cta = mergedCta;
    }
    if (options.content !== undefined || Object.keys(mergedContent).length > 0) {
      const title =
        mergedContent.title ?? options.content?.title ?? options.title ?? options.message ?? '';
      result.content = {
        ...options.content,
        ...mergedContent,
        title,
      } as NotificationContentProps;
    }
  }

  const mergedCardEvents: Partial<NotificationEventsProps> = {
    ...nestedEvents,
    ...rootEvents,
    onClick,
    onPrimaryClick: isCtaKind ? ctaOnPrimary : onPrimaryClick,
    onSecondaryClick: isCtaKind ? ctaOnSecondary : onSecondaryClick,
    primaryButtonText: isCtaKind ? undefined : primaryButtonText,
    secondaryButtonText: isCtaKind ? undefined : secondaryButtonText,
  };
  if (onCloseCard !== undefined) {
    mergedCardEvents.onClose = onCloseCard;
  }

  if (!isCtaKind) {
    if (headerPrimary) {
      mergedCardEvents.primaryButton = headerPrimary;
    }
    if (headerSecondary) {
      mergedCardEvents.secondaryButton = headerSecondary;
    }
  }

  const hasCardEvents = Object.values(mergedCardEvents).some((v) => v !== undefined);
  if (hasCardEvents) {
    result.events = mergedCardEvents as NotifyMessageOptions['events'];
  }

  return result;
}
